/**
 * AgentGraphMemoryPanel — slide-in drawer that lists shared KV memory for
 * an agent_graph workflow (§4.79 Multi-Agent Orchestration Studio).
 *
 * Backed by the REST endpoints:
 *   GET    /agent-graphs/{workflow_id}/memory/kv
 *   DELETE /agent-graphs/{workflow_id}/memory/kv/{key}
 *   DELETE /agent-graphs/{workflow_id}/memory/kv
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

function fmtVal(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v.length > 200 ? v.slice(0, 200) + '…' : v;
  try {
    const j = JSON.stringify(v);
    return j.length > 200 ? j.slice(0, 200) + '…' : j;
  } catch {
    return String(v);
  }
}

export default function AgentGraphMemoryPanel({ workflowId, onClose }) {
  const [tab, setTab] = useState('kv');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEntries = useCallback(async () => {
    if (!workflowId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/agent-graphs/${workflowId}/memory/kv`);
      setEntries(Array.isArray(res.data?.entries) ? res.data.entries : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load memory');
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    if (tab === 'kv') fetchEntries();
  }, [tab, fetchEntries]);

  const deleteOne = async (key) => {
    if (!window.confirm(`Delete memory key "${key}"?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/agent-graphs/${workflowId}/memory/kv/${encodeURIComponent(key)}`);
      fetchEntries();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear ALL shared memory for this graph? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/agent-graphs/${workflowId}/memory/kv`);
      fetchEntries();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Clear failed');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      right: 0,
      top: '70px',
      bottom: 0,
      width: '440px',
      background: 'var(--bg-secondary)',
      borderLeft: '1px solid var(--border-color)',
      boxShadow: '-2px 0 8px rgba(0,0,0,0.25)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1600,
    }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>
          🧠 Agent Graph Memory
        </span>
        <button onClick={fetchEntries} title="Refresh" style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}>↻</button>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>×</button>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)' }}>
        {['kv', 'vector'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flex: 1,
              padding: '8px',
              background: tab === t ? 'var(--bg-primary)' : 'transparent',
              border: 'none',
              borderBottom: tab === t ? '2px solid var(--accent-color)' : '2px solid transparent',
              color: tab === t ? 'var(--text-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            {t === 'kv' ? 'KV Store' : 'Vector Store'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>
        {tab === 'kv' && (
          <>
            {loading && <div style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>Loading…</div>}
            {error && <div style={{ color: 'var(--error-color)', fontSize: '12px' }}>{error}</div>}
            {!loading && !error && entries.length === 0 && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '12px', textAlign: 'center', marginTop: '20px' }}>
                No shared memory entries yet. Agents with <code>shared_memory_write</code> enabled will populate this.
              </div>
            )}
            {entries.length > 0 && (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', color: 'var(--text-primary)' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--text-tertiary)' }}>
                      <th style={{ padding: '4px', borderBottom: '1px solid var(--border-color)' }}>Key</th>
                      <th style={{ padding: '4px', borderBottom: '1px solid var(--border-color)' }}>Value</th>
                      <th style={{ padding: '4px', borderBottom: '1px solid var(--border-color)' }}>Updated</th>
                      <th style={{ padding: '4px', borderBottom: '1px solid var(--border-color)' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(e => (
                      <tr key={e.key}>
                        <td style={{ padding: '4px', borderBottom: '1px solid var(--border-color)', fontWeight: 600 }}>{e.key}</td>
                        <td style={{ padding: '4px', borderBottom: '1px solid var(--border-color)', wordBreak: 'break-word' }}>{fmtVal(e.value)}</td>
                        <td style={{ padding: '4px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{e.updated_at?.slice(0, 19)}</td>
                        <td style={{ padding: '4px', borderBottom: '1px solid var(--border-color)' }}>
                          <button onClick={() => deleteOne(e.key)} style={{ background: 'transparent', border: 'none', color: 'var(--error-color)', cursor: 'pointer', fontSize: '14px' }} title="Delete">🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  onClick={clearAll}
                  style={{ marginTop: '12px', background: 'var(--error-color)', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 10px', fontSize: '11px', cursor: 'pointer' }}
                >Clear All ({entries.length})</button>
              </>
            )}
          </>
        )}
        {tab === 'vector' && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
            Vector memory is stored in the configured vector backend (Chroma / Qdrant / PGVector) under
            collection <code>agent_graph_{workflowId}</code>. Detailed browsing is out of scope for v1 —
            agents read from this store via their <code>vector_memory_query</code> setting.
          </div>
        )}
      </div>
    </div>
  );
}
